/**
 * Car.info Filter Configuration
 * Admin-sida för att välja vilka Car.info-fält som ska visas i fordonsflottans filter
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import toast from 'react-hot-toast';

interface FilterField {
  _id: string;
  fieldId: string;
  fieldPath: string;
  displayNameSv: string;
  displayNameEn: string;
  fieldType: 'boolean' | 'string' | 'number';
  unit?: string;
  category?: string;
  source: 'specification' | 'extended_attribute' | 'basic';
  isActive: boolean;
  sortOrder: number;
  vehicleCount: number;
  sampleValues?: string[];
}

interface Stats {
  total: number;
  active: number;
  byCategory: Record<string, number>;
}

const CarInfoFilterConfig: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [fields, setFields] = useState<FilterField[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Ladda data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [fieldsRes, statsRes] = await Promise.all([
        api.get('/api/carinfo-filter-config/fields'),
        api.get('/api/carinfo-filter-config/stats'),
      ]);

      if (fieldsRes.data?.success) {
        setFields(fieldsRes.data.data);
      }
      if (statsRes.data?.success) {
        setStats(statsRes.data.data);
      }
    } catch (error) {
      console.error('Failed to load filter config:', error);
      toast.error(t('carInfoFilterConfig.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Gruppera fält efter kategori
  const groupedFields = useMemo(() => {
    const groups = new Map<string, FilterField[]>();

    for (const field of fields) {
      // Filtrera baserat på sökfråga
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          field.displayNameSv.toLowerCase().includes(query) ||
          field.displayNameEn.toLowerCase().includes(query) ||
          field.fieldId.toLowerCase().includes(query) ||
          field.category?.toLowerCase().includes(query);

        if (!matchesSearch) continue;
      }

      // Filtrera baserat på aktiv-status
      if (showActiveOnly && !field.isActive) continue;

      const category = field.category || t('carInfoFilterConfig.uncategorized');
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(field);
    }

    // Sortera kategorier: först de med aktiva fält, sedan alfabetiskt
    const sortedGroups = new Map(
      [...groups.entries()].sort((a, b) => {
        const aActiveCount = a[1].filter(f => f.isActive).length;
        const bActiveCount = b[1].filter(f => f.isActive).length;
        
        // Kategorier med aktiva fält först
        if (aActiveCount > 0 && bActiveCount === 0) return -1;
        if (aActiveCount === 0 && bActiveCount > 0) return 1;
        
        // Inom samma grupp, sortera på antal aktiva (flest först)
        if (aActiveCount !== bActiveCount) return bActiveCount - aActiveCount;
        
        // Slutligen alfabetiskt
        return a[0].localeCompare(b[0], 'sv');
      })
    );

    for (const [, categoryFields] of sortedGroups) {
      categoryFields.sort((a, b) => b.vehicleCount - a.vehicleCount);
    }

    return sortedGroups;
  }, [fields, searchQuery, showActiveOnly]);

  // Toggle ett fält
  const toggleField = async (fieldId: string, isActive: boolean) => {
    // Optimistisk uppdatering
    setFields((prev) =>
      prev.map((f) => (f.fieldId === fieldId ? { ...f, isActive } : f))
    );

    try {
      await api.put(`/api/carinfo-filter-config/toggle/${fieldId}`, { isActive });
      // Uppdatera stats
      setStats((prev) =>
        prev
          ? {
              ...prev,
              active: prev.active + (isActive ? 1 : -1),
            }
          : prev
      );
    } catch (error) {
      console.error('Failed to toggle field:', error);
      toast.error(t('carInfoFilterConfig.errorToggle'));
      // Återställ vid fel
      setFields((prev) =>
        prev.map((f) => (f.fieldId === fieldId ? { ...f, isActive: !isActive } : f))
      );
    }
  };

  // Toggle hela kategori
  const toggleCategory = async (category: string, isActive: boolean) => {
    const categoryFields = groupedFields.get(category) || [];
    const fieldIds = categoryFields.map((f) => f.fieldId);

    // Optimistisk uppdatering
    setFields((prev) =>
      prev.map((f) => (fieldIds.includes(f.fieldId) ? { ...f, isActive } : f))
    );

    try {
      await api.put('/api/carinfo-filter-config/bulk-toggle', { fieldIds, isActive });
      toast.success(`${isActive ? t('carInfoFilterConfig.activated') : t('carInfoFilterConfig.deactivated')} ${fieldIds.length} ${t('carInfoFilterConfig.fields')}`);
      // Uppdatera stats
      loadData();
    } catch (error) {
      console.error('Failed to bulk toggle:', error);
      toast.error(t('carInfoFilterConfig.errorToggle'));
      loadData();
    }
  };

  // Toggle expand kategori
  const toggleExpand = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Expandera alla kategorier
  const expandAll = () => {
    setExpandedCategories(new Set(groupedFields.keys()));
  };

  // Kollapsa alla kategorier
  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <FunnelIcon className="w-6 h-6 text-primary" />
            {t('carInfoFilterConfig.title')}
          </h1>
        </div>
        <p className="text-textSecondary">
          {t('carInfoFilterConfig.subtitle')}
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-surface rounded-lg p-4 border border-border">
            <div className="text-3xl font-bold text-primary">{stats.active}</div>
            <div className="text-textSecondary text-sm">{t('carInfoFilterConfig.activeFilters')}</div>
          </div>
          <div className="bg-surface rounded-lg p-4 border border-border">
            <div className="text-3xl font-bold text-textDefault">{stats.total}</div>
            <div className="text-textSecondary text-sm">{t('carInfoFilterConfig.totalFields')}</div>
          </div>
          <div className="bg-surface rounded-lg p-4 border border-border">
            <div className="text-3xl font-bold text-textDefault">
              {Object.keys(stats.byCategory).length}
            </div>
            <div className="text-textSecondary text-sm">{t('carInfoFilterConfig.categories')}</div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-surface rounded-lg p-4 border border-border mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Sökfält */}
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-textSecondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('carInfoFilter.searchFields')}
              className="w-full pl-10 pr-4 py-2 bg-bgLight border border-border rounded-lg text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Visa endast aktiva */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              className="w-4 h-4 text-primary rounded border-border focus:ring-primary"
            />
            <span className="text-textDefault text-sm">{t('carInfoFilterConfig.showActiveOnly')}</span>
          </label>

          {/* Expandera/Kollapsa */}
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-sm text-textSecondary hover:text-textDefault hover:bg-hover rounded transition-colors"
            >
              {t('carInfoFilterConfig.expandAll')}
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-sm text-textSecondary hover:text-textDefault hover:bg-hover rounded transition-colors"
            >
              {t('carInfoFilterConfig.collapseAll')}
            </button>
          </div>

          {/* Ladda om */}
          <button
            onClick={loadData}
            className="p-2 text-textSecondary hover:text-textDefault hover:bg-hover rounded-lg transition-colors"
            title={t('carInfoFilterConfig.reload')}
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Kategorilista */}
      <div className="space-y-2">
        {Array.from(groupedFields.entries()).map(([category, categoryFields]) => {
          const isExpanded = expandedCategories.has(category);
          const activeCount = categoryFields.filter((f) => f.isActive).length;
          const allActive = activeCount === categoryFields.length;
          const someActive = activeCount > 0 && activeCount < categoryFields.length;

          return (
            <div
              key={category}
              className="bg-surface rounded-lg border border-border overflow-hidden"
            >
              {/* Kategori-header */}
              <div
                className="flex items-center gap-3 px-4 py-3 bg-hover/50 cursor-pointer"
                onClick={() => toggleExpand(category)}
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-textSecondary" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-textSecondary" />
                )}

                <span className="font-medium text-textDefault flex-1">{category}</span>

                <span className="text-sm text-textSecondary mr-2">
                  {categoryFields.length} {t('carInfoFilterConfig.fieldsCount')}
                </span>

                {/* Kategori checkbox - till höger */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategory(category, !allActive);
                  }}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                    allActive
                      ? 'bg-primary border-black'
                      : someActive
                      ? 'bg-primary/50 border-black'
                      : 'border-black hover:border-primary'
                  }`}
                >
                  {(allActive || someActive) && (
                    <CheckIcon className="h-3 w-3 text-white" />
                  )}
                </div>
              </div>

              {/* Fält i kategorin */}
              {isExpanded && (
                <div className="divide-y divide-border">
                  {categoryFields.map((field) => (
                    <div
                      key={field.fieldId}
                      className="flex items-center gap-4 px-4 py-3 hover:bg-hover/30 transition-colors"
                    >
                      {/* Fältinfo */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-textDefault">
                            {field.displayNameSv}
                          </span>
                          {field.unit && (
                            <span className="text-xs text-textSecondary bg-hover px-1.5 py-0.5 rounded">
                              {field.unit}
                            </span>
                          )}
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded ${
                              field.fieldType === 'boolean'
                                ? 'bg-green-100 text-green-700'
                                : field.fieldType === 'number'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {field.fieldType}
                          </span>
                        </div>
                        {field.sampleValues && field.sampleValues.length > 0 && (
                          <div className="text-sm text-textSecondary truncate">
                            {t('carInfoFilterConfig.examplePrefix')}: {field.sampleValues.slice(0, 5).join(', ')}
                          </div>
                        )}
                      </div>

                      {/* Checkbox - till höger */}
                      <div
                        onClick={() => toggleField(field.fieldId, !field.isActive)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                          field.isActive
                            ? 'bg-primary border-black'
                            : 'border-black hover:border-primary'
                        }`}
                      >
                        {field.isActive && <CheckIcon className="h-3 w-3 text-white" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {groupedFields.size === 0 && (
          <div className="text-center py-12 text-textSecondary">
            <FunnelIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{t('carInfoFilterConfig.noFieldsFound')}</p>
            {searchQuery && (
              <p className="text-sm mt-1">
                {t('carInfoFilterConfig.adjustSearch')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarInfoFilterConfig;
